/**
 * Y2 ONBOARD (CLAUDE.md sections 3, 5, 10). The vocabulary of the onboarding run, in one place so the
 * table CHECKs, the event catalog and the handler read one list.
 *
 * The run is an ORCHESTRATOR with recorded steps. The worker never reads the raw tier (section 10): the
 * step that does, the pipeline over the Meta export, is run by a person on the pipeline service, and the
 * worker only sees its DISTILLED output (the artifact bundle and the numbers-only agenda summary). It does not read
 * the processed tier either: the agenda is built pipeline-side and consumed here. Every
 * artifact the run writes is a DRAFT: approving one is T2, a human act (section 3).
 */

/** The states of one onboarding run, in the order a run moves through them. `halted` is the loud stop. */
export const ONBOARD_STATES = [
  "planned",
  "awaiting_export",
  "awaiting_pipeline",
  "drafting",
  "agenda",
  "awaiting_interview",
  "candidates",
  "done",
  "halted",
] as const;
export type OnboardState = (typeof ONBOARD_STATES)[number];

/** States in which a run is still live: at most one such run per client (a partial unique index). */
export const ONBOARD_ACTIVE_STATES: readonly OnboardState[] = ONBOARD_STATES.filter((s) => s !== "done" && s !== "halted");

/** The step names a task payload may name (`{runId, step?}`): one per state that does work. */
export const ONBOARD_STEPS = ["planned", "awaiting_export", "awaiting_pipeline", "drafting", "agenda", "awaiting_interview", "candidates"] as const;
export type OnboardStep = (typeof ONBOARD_STEPS)[number];

/** Why a run halted: codes, never prose. A halted run is not retried; a person reads the reason and starts another. */
export const ONBOARD_HALT_REASONS = [
  "bundle_refused",
  "artifacts_dir_refused",
  "drafts_refused",
  "agenda_refused",
  "candidates_refused",
  "step_failed",
] as const;
export type OnboardHaltReason = (typeof ONBOARD_HALT_REASONS)[number];

/** The three artifact kinds the run drafts (the brief: voice, playbook, kb). Gold and eval stay with `publish:artifacts`. */
export const ONBOARD_DRAFT_KINDS = ["voice", "playbook", "kb"] as const;
export type OnboardDraftKind = (typeof ONBOARD_DRAFT_KINDS)[number];

/** The agent the run writes under: autolearn holds onboarding (agentRoster DUTIES `autolearn.onboard`). */
export const ONBOARD_AGENT = { id: "autolearn", version: "onboard-0.1.0", duty: "autolearn.onboard" } as const;

/** The owner's first fortnight: the window the owner-edit rate is read over. AWAITING RATIFICATION (Y2 funnel definition). */
export const ONBOARD_EDIT_RATE_WINDOW_DAYS = 14;

/**
 * The funnel metric definitions, as the page and the reader both say them. AWAITING RATIFICATION: Kabir has not
 * ruled on either, and a number is only as good as what it is defined to count.
 */
export const ONBOARD_FUNNEL_DEFINITIONS = {
  timeToLive:
    "AWAITING RATIFICATION. Time to live: from the run's onboard.started event to the first draft_transmissions row for the client in a settled transmitted state. Null until that row exists. It measures the whole path to a first message that left the building, not the length of any one step.",
  ownerEditRate:
    "AWAITING RATIFICATION. Owner-edit rate: of the client's decided drafts in the first 14 days from onboard.started, the share whose approval was a substantive edit (F-05: anything touching facts, offer, price or intent, or the one-tap substantive flag; cosmetic changes up to the 10 percent delta do not count). Null while no draft has been decided in the window.",
} as const;
